import {
  getBrowserData,
  getWindowDimensions,
  log,
  newGuid,
  sleep,
} from '@ms-ows/logging'

import { getTargetElementInfoWithFallback } from '../../dom'
import { ActionType, MultiSurfaceParams, SurfaceParams } from '../../models'
import { normalizeSurfaceParams } from '../../surface-params'
import { addWindowEventListener, removeWindowEventListener } from '../../utils'
import {
  performActionBeaconCallback,
  performImpressionCallback,
} from '../beacon'
import {
  beforeUnload,
  isMultiSurfaceParams,
  SurfaceLaunchType,
} from '../helpers'
import { preloadResources } from '../preload'
import {
  getLaunchTime,
  getPageContext,
  getRethrowExceptions,
  setLaunchTime,
} from '../state'
import { logElapsedEvent } from '../telemetry'

import { handleClickAction } from './actions'
import { renderHostMultiple } from './host'
import { renderMultipleSurfaces } from './multiple'
import { renderSingle } from './single'

const WEB_SURFACE_EVENT_NAME = 'FGWebSurfaceEvent'

const onSurfaceLaunched =
  (
    renderTargetId: string,
    renderStartTime: number,
    surfaceId: string | undefined,
    surfaceParams: SurfaceParams | MultiSurfaceParams
  ) =>
  (
    eventName: SurfaceLaunchType = 'SurfaceLaunched',
    result = true,
    error?: string
  ) => {
    setLaunchTime(Date.now())

    log.event(0x2360288c /* tag_9yc8m */, eventName, {
      customData: {
        // metadata
        creativeId: surfaceParams.creativeId as string,
        payloadId: surfaceParams.payloadId as string,
        surfaceId,
        surfaceName: surfaceParams.surfaceName as string,

        // additional data
        elapsedTime: getLaunchTime() - renderStartTime,
        error: error || '',
        result,
        surfaceType: surfaceParams.surfaceType,
        ...getBrowserData(),
        ...getWindowDimensions(),

        // surface telemetry
        isAA: surfaceParams.isAA as boolean,
        isControl: surfaceParams.isControl as boolean,
        telemetryEventName: surfaceParams.telemetryEventName as string,
        treatmentType: surfaceParams.treatmentType as number,
      },
    })

    dispatchWebSurfaceEvent('Opened', renderTargetId, surfaceId, surfaceParams)

    if (!error) {
      performImpressionCallback(surfaceParams)
    }
  }

const dispatchWebSurfaceEvent = (
  actionType: 'Opened' | 'Updated' | 'Dismissed',
  renderTargetId: string,
  surfaceId: string | undefined,
  surfaceParams: SurfaceParams | MultiSurfaceParams,
  result = true
) => {
  const eventObj = {
    detail: {
      action: actionType,
      creativeId: surfaceParams.creativeId as string,
      id: surfaceId,
      isAA: surfaceParams.isAA as boolean,
      isControl: surfaceParams.isControl as boolean,
      isHidden: isSurfaceHidden(surfaceParams),
      payloadId: surfaceParams.payloadId as string,
      success: result,
      surfaceType: surfaceParams.surfaceType,
      telemetryEventName: surfaceParams.telemetryEventName as string,
      treatmentType: surfaceParams.treatmentType as number,

      renderTargetId,
    },
  }

  try {
    document.dispatchEvent(new CustomEvent(WEB_SURFACE_EVENT_NAME, eventObj))
  } catch (err) {
    log.error(0x2361a80f /* tag_9y06p */, 'failed to dispatch event', err)
  }
}

// log an error if the surface was not rendered as expected
const verifySurfaceRendering = (
  surfaceParams: SurfaceParams | MultiSurfaceParams
) => {
  if (isSurfaceHidden(surfaceParams)) {
    return
  }
  const targetNode = document.documentElement || document.body
  const config = { attributes: true, childList: true, subtree: true }

  const surfaceContainerElements = [
    // Coachmark
    "div[class^='entityHost']",
    "div[class^='pulsingBeacon']",
    // TUI
    "div[class~='ms-PositioningContainer-layerHost]",
    "div[class^='calloutMain']",
    "div[aria-roledescription='Teaching notification']",
    "div[class~='ms-TeachingBubble-content']",
  ]
  let isSurfaceRendered = false
  const fullRenderCheck = surfaceParams.fullRenderCheck || false
  const renderedSurfaceDelay =
    surfaceParams.renderCheckDelay || (fullRenderCheck ? 5000 : 2000)

  // mutation observer is used if doing a full render check
  const callback = (mutationsList: MutationRecord[]) => {
    for (const mutation of mutationsList) {
      if (
        surfaceContainerElements.some(
          e => (mutation.target as HTMLElement).querySelectorAll(e).length === 1
        )
      ) {
        isSurfaceRendered = true
        observer.disconnect()

        return
      }
    }
  }
  const observer = new MutationObserver(callback)
  if (fullRenderCheck) {
    observer.observe(targetNode, config)
  }

  sleep(renderedSurfaceDelay as number)
    .then(() => {
      if (!fullRenderCheck) {
        isSurfaceRendered = surfaceContainerElements.some(
          e => document.querySelectorAll(e).length === 1
        )
      }
      if (!isSurfaceRendered) {
        if (fullRenderCheck) {
          observer.disconnect()
        }
        log.error(
          0x1f784149 /* tag_54efj */,
          `failed to render surface ${surfaceParams.surfaceType}`,
          surfaceParams.creativeId
        )
      }
    })
    .catch((err: unknown) =>
      log.error(
        0x1f78414a /* tag_54efk */,
        'failed to log surface rendering',
        err
      )
    )
}

export const isSurfaceHidden = (
  surfaceParams: SurfaceParams | MultiSurfaceParams
) => {
  return (
    surfaceParams.isAA ||
    surfaceParams.isControl ||
    surfaceParams.isControl1 ||
    surfaceParams.treatmentType === 0
  )
}

// log an error if the anchor exists but is not visible (eg. < 1 px)
const verifyTargetElement = (
  surfaceParams: SurfaceParams | MultiSurfaceParams
) => {
  if (!surfaceParams || isSurfaceHidden(surfaceParams)) {
    return
  }

  try {
    const { selector, targetElement } = getTargetElementInfoWithFallback(
      surfaceParams.anchorElementSelectors as string[]
    )

    if (!targetElement) {
      return
    }

    const properties = targetElement.getBoundingClientRect()
    if (properties.height < 1 || properties.width < 1) {
      log.error(
        0x1f60409c /* tag_5yec2 */,
        `target element ${selector} of surface ${surfaceParams.surfaceType} is hidden`,
        surfaceParams.creativeId
      )

      return
    }
  } catch (err: unknown) {
    log.error(
      0x1f60409d /* tag_5yec3 */,
      `failed to verify target element of surface ${surfaceParams.surfaceType}, ${surfaceParams.creativeId}`,
      err
    )
  }
}

// tslint:disable-next-line: max-func-body-length
export const renderSurface = async (
  surfaceParams: SurfaceParams | MultiSurfaceParams,
  containerId?: string,
  surfaceId?: string,
  impressionId?: string
) => {
  log.updateCustomData({ impressionId: impressionId ?? newGuid() })

  const renderTargetId = containerId || 'obf-WebSurfacesContainer'

  if (!surfaceParams) {
    log.error(0x2360288d /* tag_9yc8n */, 'surface params are not provided')

    return
  }

  normalizeSurfaceParams(surfaceParams)

  const surfaceLaunchedEvent = onSurfaceLaunched(
    renderTargetId,
    Date.now(),
    surfaceId,
    surfaceParams
  )

  try {
    if (!isSurfaceHidden(surfaceParams)) {
      addWindowEventListener('beforeunload', beforeUnload)
      const preFetchDelay = surfaceParams.preFetchDelay || 10
      sleep(preFetchDelay as number)
        .then(() => {
          preloadResources(surfaceParams)
        })
        .catch((error: unknown) =>
          log.error(
            0x2360288e /* tag_9yc8o */,
            'failed to pre-load resources',
            error
          )
        )
    }

    const onClosed = (actionType: ActionType) => {
      logElapsedEvent(
        log.reserve(0x2360288f /* tag_9yc8p */),
        'SurfaceCompleted',
        actionType,
        0,
        getLaunchTime(),
        surfaceId
      )

      removeWindowEventListener('beforeunload', beforeUnload)
      performActionBeaconCallback(actionType, surfaceParams)

      dispatchWebSurfaceEvent(
        'Dismissed',
        renderTargetId,
        surfaceId,
        surfaceParams
      )

      if (!surfaceParams.useNativeRender && renderCallback) {
        // host rendering
        handleClickAction(
          log.reserve(0x217d2849 /* tag_75s7j */),
          'close',
          undefined,
          surfaceParams.surfaceType,
          (surfaceParams as SurfaceParams).closeButtonAction,
          (surfaceParams as SurfaceParams).closeButtonActionParams
        )
      }
    }

    const onUpdated = () =>
      dispatchWebSurfaceEvent(
        'Updated',
        renderTargetId,
        surfaceId,
        surfaceParams
      )

    const { renderCallback } = getPageContext()

    if (isMultiSurfaceParams(surfaceParams)) {
      if (!surfaceParams.useNativeRender && renderCallback) {
        await renderHostMultiple(
          getLaunchTime(),
          onClosed,
          onUpdated,
          renderCallback,
          renderTargetId,
          surfaceId,
          surfaceLaunchedEvent,
          surfaceParams
        )
      } else {
        await renderMultipleSurfaces(
          surfaceParams,
          renderCallback,
          1,
          renderTargetId,
          surfaceLaunchedEvent,
          onUpdated,
          onUpdated,
          onClosed
        )
      }
    } else {
      await renderSingle(
        surfaceParams,
        renderTargetId,
        true, // for single surfaces removeSurface on target element click
        renderCallback,
        surfaceLaunchedEvent,
        onUpdated,
        onClosed
      )
    }

    verifySurfaceRendering(surfaceParams)
    verifyTargetElement(surfaceParams)
  } catch (error) {
    let eventName = 'SurfaceLaunched'
    if (surfaceParams.treatmentType === 0 || surfaceParams.isControl) {
      eventName = 'SurfaceControlTriggered'
    } else if (surfaceParams.isAA) {
      eventName = 'SurfaceAATriggered'
    }

    surfaceLaunchedEvent(
      eventName as SurfaceLaunchType,
      false,
      // tslint:disable-next-line: no-unsafe-any
      error && error.message
    )

    if (getRethrowExceptions()) {
      throw error
    }
  }
}
